import * as React from "react";
import { Grid, Paper, Typography, Box, TextField } from "@mui/material";
import { StyledActivityRow } from "./styled";
import IPersonRow from "./interface";
import { InspectorModel } from "@models/InspectorModel";
import InspectorsService from "@services/InspectorsService";
import MultiButtonMolecule from "@molecules/MultiButtonMolecule";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import { ratesFeaturesEnabled } from "@utils/features";

type RateField = "default_daily_rate" | "default_hourly_rate";

const toInput = (value: number | null | undefined): string =>
  value === null || value === undefined ? "" : String(value);

/**
 * Inline-editable default rate cell. Only internal inspectors have a fixed
 * default rate, so for external inspectors the cell renders a dash.
 * On blur / Enter it persists via the lightweight `PATCH /inspectors/{id}/rate`
 * endpoint and reverts on error.
 */
const RateCell = ({
  person,
  field,
}: {
  person: InspectorModel;
  field: RateField;
}) => {
  const [value, setValue] = React.useState<string>(toInput(person[field]));
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<boolean>(false);

  React.useEffect(() => {
    setValue(toInput(person[field]));
    setError(false);
    // Re-sync when the underlying inspector row changes identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id]);

  if (!person.internal) {
    return (
      <Typography sx={{ textAlign: "center", color: "text.disabled" }}>
        —
      </Typography>
    );
  }

  const save = () => {
    const persisted = toInput(person[field]);
    if (value === persisted) {
      return;
    }
    const parsed = value === "" ? null : Number(value);
    if (parsed !== null && (isNaN(parsed) || parsed < 0)) {
      setError(true);
      return;
    }
    setSaving(true);
    setError(false);
    InspectorsService.setRate(person.id, { [field]: parsed })
      .then((res: any) => {
        setSaving(false);
        const ok =
          res && typeof res.hasErrors === "function" && !res.hasErrors();
        if (ok) {
          // Reflect the saved value on the instance so other reads stay in sync.
          (person as any)[field] = parsed;
          setValue(toInput(parsed));
        } else {
          setError(true);
          setValue(persisted);
        }
      })
      .catch(() => {
        setSaving(false);
        setError(true);
        setValue(persisted);
      });
  };

  return (
    <TextField
      value={value}
      type="number"
      size="small"
      variant="standard"
      disabled={saving}
      error={error}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          (e.target as HTMLInputElement).blur();
        }
      }}
      inputProps={{ min: 0, step: "0.01", style: { textAlign: "center" } }}
      sx={{ width: "100%" }}
    />
  );
};

const PeopleRowMolecule = ({ person, onActionSelected }: IPersonRow) => {
  const showRates = ratesFeaturesEnabled();
  const actionSelected = (item: any) => {
    if (onActionSelected) {
      onActionSelected(item, person);
    }
  };

  return (
    <>
      <Paper sx={{ border: "0px" }} elevation={0} variant="outlined">
        <StyledActivityRow>
          <Grid container sx={{ alignItems: "center" }}>
            <Grid
              item
              sx={{ display: "flex", alignItems: "center" }}
              md={1}
              xs={12}
            >
              <Box sx={{ margin: "auto" }}>
                <Typography>
                  {person.verified ? (
                    <CheckCircleOutlinedIcon color="primary" />
                  ) : (
                    <ErrorOutlinedIcon color="warning" />
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              sx={{ display: "flex", alignItems: "center" }}
              md={1}
              xs={12}
            >
              <Box sx={{ margin: "auto" }}>
                <Typography>
                  {person.internal ? (
                    <CheckCircleOutlinedIcon color="primary" />
                  ) : (
                    <ErrorOutlinedIcon color="warning" />
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item md={2} xs={12}>
              {person.full_name ? person.full_name : "--"}
            </Grid>
            <Grid item md={showRates ? 1 : 2} xs={12}>
              <ul>
                {person.partners &&
                  person.partners.map((partner: any, index: number) => {
                    return (
                      <li key={index}>{`${partner.category.name}${
                        partner.category.alias === "provider" ? ":" : ""
                      } ${partner.partner ? partner.partner.name : ""}`}</li>
                    );
                  })}
              </ul>
            </Grid>
            <Grid item md={2} xs={12}>
              {person.country ? person.country.name : "--"}
            </Grid>
            <Grid item md={showRates ? 1 : 2} xs={12}>
              {person.city ? person.city : "--"}
            </Grid>
            {showRates && (
              <>
                <Grid item md={1} xs={12} sx={{ paddingX: "4px" }}>
                  <RateCell person={person} field="default_daily_rate" />
                </Grid>
                <Grid item md={1} xs={12} sx={{ paddingX: "4px" }}>
                  <RateCell person={person} field="default_hourly_rate" />
                </Grid>
              </>
            )}
            <Grid sx={{ textAlign: "right" }} item md={2} xs={12}>
              <MultiButtonMolecule
                onSelected={actionSelected}
                options={[
                  {
                    label: "Details",
                    id: 1,
                    data: { personId: person.id },
                  },
                  {
                    label: "Delete",
                    id: 4,
                    data: { personId: person.id },
                  },
                ]}
              />
            </Grid>
          </Grid>
        </StyledActivityRow>
      </Paper>
    </>
  );
};

export default PeopleRowMolecule;
